import React, { useCallback, useEffect, useState } from 'react';
import { useEvent, useLazyCommand } from '@soap/modules';
import { Plus } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import {
  externalProviderKinds,
  type ExternalProviderKind,
  type UnitContentSource,
  unitContentSources
} from './createUnitTypes';

export type ExternalProviderOption = {
  id: string;
  displayName: string;
  providerKind: string;
};

interface ExternalResourceProviderSelectProps {
  contentSource: UnitContentSource;
  value: string;
  onChange: (providerId: string) => void;
  error?: string;
}

function defaultKindForSource(source: UnitContentSource): ExternalProviderKind {
  return source === unitContentSources.externalOnline
    ? externalProviderKinds.onlineProvider
    : externalProviderKinds.bookPublisher;
}

export function ExternalResourceProviderSelect({
  contentSource,
  value,
  onChange,
  error
}: ExternalResourceProviderSelectProps) {
  const [providers, setProviders] = useState<ExternalProviderOption[]>([]);
  const [loadProviders] = useLazyCommand('C328v1_GetExternalResourceProviders');
  const [addProvider] = useLazyCommand('C329v1_AddExternalResourceProvider');
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const refresh = useCallback(() => {
    loadProviders({});
  }, [loadProviders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEvent({
    eventName: 'E328v1_GotExternalResourceProviders',
    onEventReceived: (event: Record<string, unknown>) => {
      const raw = (event.e328_Providers ?? event.E328_Providers) as
        | Array<Record<string, unknown>>
        | undefined;
      const mapped = (raw ?? [])
        .map(row => ({
          id: String(row.e328_ProviderId ?? row.E328_ProviderId ?? ''),
          displayName: String(row.e328_DisplayName ?? row.E328_DisplayName ?? ''),
          providerKind: String(row.e328_ProviderKind ?? row.E328_ProviderKind ?? '')
        }))
        .filter(p => p.id && p.displayName);
      setProviders(mapped);
    }
  });

  useEvent({
    eventName: 'E329v1_ExternalResourceProviderAdded',
    onEventReceived: (event: Record<string, unknown>) => {
      const id = String(event.e329_ProviderId ?? event.E329_ProviderId ?? '');
      if (id) onChange(id);
      setShowAdd(false);
      setNewName('');
      setAdding(false);
      refresh();
    }
  });

  const filtered = providers.filter(p => {
    const kind = defaultKindForSource(contentSource);
    return !p.providerKind || p.providerKind === kind;
  });

  const handleAdd = () => {
    const name = newName.trim();
    if (!name || adding) return;
    setAdding(true);
    addProvider({
      c329_DisplayName: name,
      c329_ProviderKind: defaultKindForSource(contentSource)
    });
  };

  return (
    <div className="mb-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Provider</h3>
      <p className="text-sm text-gray-600 mb-2">
        Select the book publisher or online provider for these lessons.
      </p>
      {error ? (
        <div className="text-destructive-action text-sm mb-2 bg-destructive-action/10 border border-destructive-action/30 rounded p-2">
          {error}
        </div>
      ) : null}
      <div className="flex gap-2">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1 p-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-action-blue focus:border-action-blue"
        >
          <option value="">Select a provider...</option>
          {filtered.map(p => (
            <option key={p.id} value={p.id}>
              {p.displayName}
            </option>
          ))}
        </select>
        <Button type="button" variant="outline" onClick={() => setShowAdd(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add provider</DialogTitle>
          </DialogHeader>
          <Input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Provider name"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAdd} disabled={!newName.trim() || adding}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
